import { useCallback, useEffect } from "react";
import { isWebView } from "@/utilities/display";
import { getBridge } from "@/utilities/bridge";
import { getDeviceId } from "@/utilities/getDeviceId";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { getBiometricStatus } from "../services/biometrics";
import { useUserToken } from "./useUserToken";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { useRemoteConfig } from "@/hooks/remoteConfig/useRemoteConfig";

export const useIsBiometricsEnabled = () => {
  const { loading, value } = useRemoteConfig("biometrics_v2_enabled");

  return { isEnabled: !loading && (value?.asBoolean() ?? false) };
};

export const useIsBiometricAvailable = () => {
  const { isEnabled } = useIsBiometricsEnabled();
  const { available } = useShellStore(
    (state) => state.deviceBiometricAvailability,
  );

  return isEnabled && isWebView() && available;
};

export const useBiometricSync = () => {
  const { isEnabled } = useIsBiometricsEnabled();
  const token = useUserToken();
  const { available, enrolled } = useShellStore(
    (state) => state.deviceBiometricAvailability,
  );
  const isBiometricProcessing = useShellStore(
    (state) => state.isBiometricProcessing,
  );

  useEffect(() => {
    const sync = async () => {
      if (
        !isEnabled ||
        !isWebView() ||
        !token ||
        !available ||
        isBiometricProcessing
      )
        return;

      const result = await getBiometricStatus(token);

      if (result.ok) {
        const apiEnrolled = result.data.devices.some(
          (d) => d.deviceId === getDeviceId(),
        );

        if (apiEnrolled !== enrolled) {
          // local key exists but the server has no record of this device:
          // only clear the local key so we don't touch another device's enrollment
          getBridge().send.biometricReset({
            token,
            skipApi: enrolled && !apiEnrolled,
          });
        }
      }
    };

    sync();
  }, [isEnabled, token, available, enrolled, isBiometricProcessing]);
};

export const useBiometrics = () => {
  const token = useUserToken();
  const setIsBiometricProcessing = useShellStore(
    (state) => state.setIsBiometricProcessing,
  );

  const enroll = useCallback(
    (source: "prompt" | "details_page") => {
      if (!isWebView() || !token) return;

      setIsBiometricProcessing(true);
      getBridge().send.biometricEnroll({ token, source });
    },
    [token],
  );

  const revoke = useCallback(() => {
    if (!isWebView() || !token) return;

    setIsBiometricProcessing(true);
    getBridge().send.biometricReset({ token });
  }, [token]);

  const sign = useCallback((challenge: string, challengeId: string) => {
    if (!isWebView()) return;

    getBridge().send.biometricSign({ challenge, challengeId });
  }, []);

  return { enroll, revoke, sign };
};

export const dismissBiometricPrompt = (userId?: string) => {
  if (!userId) return;
  localStorage.setItem(`biometricSkipped:${userId}`, "true");
};

const isBiometricPromptDismissed = (userId?: string): boolean => {
  if (!userId) return false;
  return localStorage.getItem(`biometricSkipped:${userId}`) === "true";
};

export const useBiometricPromptCheck = () => {
  const { isEnabled } = useIsBiometricsEnabled();
  const { available, enrolled } = useShellStore(
    (state) => state.deviceBiometricAvailability,
  );

  const userId = useUserId();
  const { profile, isLoading } = useProfile();
  const hasProfile = !!profile;

  const setModal = useShellStore((state) => state.setModal);
  const modal = useShellStore((state) => state.modal);
  const hasModalOpened = !!modal;

  useEffect(() => {
    if (!isEnabled) return;

    // check device capability
    if (!isWebView() || !available || enrolled) return;

    // check biometric state
    const hasSkipped = isBiometricPromptDismissed(userId);
    if (hasSkipped || useShellStore.getState().isBiometricProcessing) return;

    // check if user is ready
    if (!userId || isLoading || !hasProfile || hasModalOpened) return;

    setModal("enableBiometrics");
  }, [
    isEnabled,
    available,
    enrolled,
    userId,
    isLoading,
    hasProfile,
    hasModalOpened,
    setModal,
  ]);
};
